import { memo, useLayoutEffect } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Stack, Skeleton, Typography } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import {
  emptyId,
  isEmptyId,
  useEditorStore,
  DisconnectedIllustration,
  useFlowBuilder2Context,
  useFlowBuilder2GetOrCreateDraftLayoutMutation,
} from '@diana/core';
import { ILayoutBuilderProps } from './LayoutBuilder.types';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { ComponentBuilder } from '../ComponentBuilder/ComponentBuilder';
import { ComponentBuilderDropPlaceholder } from '../ComponentBuilder/ComponentBuilderDropPlaceholder';
import { ContainerTypeComponent } from '../../../../../providers/DndLayoutComponents2';
import { DndDropArea } from '../DndDropArea/DndDropArea';

export const LayoutBuilder = memo((props: ILayoutBuilderProps) => {
  const { layoutId, taskId, stepId, flowId } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const { updateAndSaveTaskById } = useFlowBuilder2Context();

  const layoutExists = useEditorStore((s) => !!s.getLayoutById(layoutId));
  const setLazyLayout = useEditorStore((s) => s.setLazyLayout);
  const componentsCount = useEditorStore((s) => {
    return s.getLayoutById(layoutId)?.components.length ?? 0;
  });

  const items = useEditorStore((s) => {
    return (
      s.getLayoutById(layoutId)?.components?.map((component, index) => {
        const width = component?.properties?.width ?? 'col-12';
        return {
          index,
          parentId: component.parentId,
          componentId: component.id,
          componentName: component.component,
          componentSize: Number(width.replace('col-', '') ?? 12),
        };
      }) ?? []
    ).filter((c) => isEmptyId(c.parentId));
  }, isEqual);

  const {
    mutate: getOrCreateDraftLayout,
    error,
    isPending: isLoading,
  } = useFlowBuilder2GetOrCreateDraftLayoutMutation({
    onSuccess: (layout, variables) => {
      setLazyLayout(layout.id, layout);
      if (taskId && variables.layoutId !== layout.id) {
        updateAndSaveTaskById(taskId, (draft) => {
          draft.layoutId = layout.id;
          return draft;
        });
      }
    },
  });

  useLayoutEffect(() => {
    if (!isEmptyId(layoutId)) {
      getOrCreateDraftLayout({ flowId, layoutId });
    }
  }, [flowId, layoutId, getOrCreateDraftLayout]);

  if (isEmptyId(layoutId))
    return (
      <Box sx={{ px: 2, py: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box textAlign="center">
          <WarningIcon />
          <Typography color="text.secondary">{t('descriptions.layoutIsMissing')}</Typography>
        </Box>
      </Box>
    );

  if (!layoutExists && isLoading)
    return (
      <Box pt={1}>
        <Skeleton variant="rounded" height={200} />
      </Box>
    );

  if (error)
    return (
      <Stack p={2} alignItems="center">
        <DisconnectedIllustration sx={{ maxHeight: '120px' }} />
        <Typography>{t('errors.loadLayoutFailed')}</Typography>
        <Typography>{layoutId}</Typography>
      </Stack>
    );

  return (
    <Box data-layout-id={layoutId} id={layoutId} sx={{ ml: 0.5, mt: 0.5 }}>
      {items.length ? (
        <Grid container spacing={2} position="relative" zIndex={1}>
          {items.map((item) => (
            <ComponentBuilder
              key={item.componentId}
              layoutId={layoutId}
              parentId={item.parentId}
              componentId={item.componentId}
              componentName={item.componentName}
              componentSize={item.componentSize}
              componentIndex={item.index}
              taskId={taskId}
              stepId={stepId}
              flowId={flowId}
            />
          ))}
          <ComponentBuilderDropPlaceholder
            accept={ContainerTypeComponent}
            location={{
              layoutId,
              parentId: emptyId,
              index: items.length ? items[items.length - 1].index + 1 : componentsCount,
            }}
            layoutId={layoutId}
            taskId={taskId}
            stepId={stepId}
            flowId={flowId}
          />
        </Grid>
      ) : (
        <DndDropArea
          index={items.length}
          layoutId={layoutId}
          parentId={emptyId}
          taskId={taskId}
          stepId={stepId}
          flowId={flowId}
        />
      )}
    </Box>
  );
}, isEqual);
